import {
  ORDER_SOURCE_LABELS,
  WMS_ORDER_STATE_LABELS,
  formatQuantity,
  orderSourceLabel,
  wmsOrderStateLabel,
} from '../../utils/inventoryLabels';

export const ALL_FILTER = 'all';

/**
 * The states as the chips show them: the happy path in its order, so the row
 * reads as the way an order walks through the warehouse; and the ends apart,
 * because they are exceptions, not stages, and putting them beside the stages
 * invites reading them as if an order moved towards them.
 */
export const FLOW_STATES = ['entered', 'reserved', 'picking', 'picked', 'packed', 'labeled'];
export const CLOSED_STATES = ['dispatched', 'delivered', 'cancelled', 'returned', 'failed'];

/** Still some floor work to do. */
export const isOpenState = (state) => FLOW_STATES.includes(state);

/**
 * Cancelling is possible until it leaves: up to labelled, inclusive (the label
 * is then voided at the courier too). From dispatched on, it is a return.
 */
export const isCancellableState = (state) => FLOW_STATES.includes(state);

/**
 * Whether "Reservar" makes sense on its own: an order that entered and holds
 * nothing yet. Once something is reserved the rest comes in by itself when
 * goods arrive, and topping it up is the preparing screen's job.
 */
export const isReservableState = (state) => state === 'entered';

/**
 * The row rules the buttons and the bulk bar share, so ticking a row can
 * never do more than its own buttons would.
 */
export const rowCanCancel = (row, canCancel) =>
  Boolean(canCancel) && row.module_active !== false && isCancellableState(row.wms_state);

export const rowCanReserve = (row, canReserveHere) =>
  Boolean(canReserveHere) && row.module_active !== false && isReservableState(row.wms_state);

const BULK_PROBLEMS_SHOWN = 3;

/** «#1043: Orden no encontrada · #1044: ya salió · y 2 más». */
export const bulkProblemsText = (problems = []) => {
  if (problems.length === 0) return '';
  const shown = problems.slice(0, BULK_PROBLEMS_SHOWN).map((item) => `#${item.id}: ${item.message}`);
  const rest = problems.length - shown.length;
  return [...shown, rest > 0 ? `y ${rest} más` : null].filter(Boolean).join(' · ');
};

/**
 * What to say after a bulk: the server's count, the units when reserving,
 * and the problems by order. A bulk where nothing went through is an error.
 */
export const bulkDoneNotice = (result) => {
  const done = (result.reserved || result.cancelled || []).length;
  const parts = [result.message];
  if (Number(result.units) > 0) parts.push(`${formatQuantity(result.units)} un`);
  const problems = bulkProblemsText(result.problems);
  if (problems) parts.push(problems);
  return { type: done > 0 ? 'success' : 'error', message: parts.join(' · ') };
};

export const STATE_OPTIONS = Object.entries(WMS_ORDER_STATE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const SOURCE_OPTIONS = [
  { value: ALL_FILTER, label: 'Todos' },
  ...Object.entries(ORDER_SOURCE_LABELS).map(([value, label]) => ({ value, label })),
];

export const formatOrderMoment = (value) =>
  value
    ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

export const formatOrderDate = (value) =>
  value ? new Date(value).toLocaleDateString('es-CL', { dateStyle: 'medium' }) : '-';

/** "#1042 · WEB-1042", or just the number. */
export const orderTitle = (order) =>
  `#${order.id}${order.reference_id ? ` · ${order.reference_id}` : ''}`;

/**
 * How much of the order is prepared, as a share. `null` when there is nothing
 * to prepare: an order that came in with no product lines — the sheet and the
 * manual form create none — has no bar to draw, and a bar at zero would lie.
 */
export const preparedShare = (order) => {
  if (!order.has_lines) return null;
  const requested = Number(order.requested_units || 0);
  if (!requested) return 0;
  return Math.round((Number(order.prepared_units || 0) / requested) * 100);
};

/**
 * What cancelling does to this order, said with its numbers: the order is
 * kept, the reservations go back to available, and what was already picked
 * stays on the table to be put away again.
 *
 * @returns {string[]} One sentence per consequence.
 */
export const cancelConsequences = (order) => {
  const lines = [
    `La orden ${orderTitle(order)} pasará a Cancelada. No se elimina: conserva su historial.`,
  ];
  const reserved = Number(order.reserved_units || 0);
  if (reserved > 0) {
    lines.push(`${formatQuantity(reserved)} un que estaban reservadas vuelven a disponible.`);
  }
  const prepared = Number(order.prepared_units || 0);
  if (prepared > 0) {
    lines.push(
      `${formatQuantity(prepared)} un ya pickeadas quedan en la mesa de preparación y aparecen en Almacenado → Por reponer.`
    );
  }
  if (order.has_label) {
    lines.push('Ya tiene etiqueta del courier: se intentará cancelar allá también.');
  }
  return lines;
};

/**
 * What the server answered to a cancellation, as the notice to show. A courier
 * that could not be told is a warning, not a success: somebody has to do it by hand.
 *
 * @param {{order: object, released_units: number, staged_units: number, courier: string|null}} result
 */
export const cancelDoneNotice = (result) => {
  const parts = [`Orden #${result.order.id} cancelada`];
  if (result.released_units > 0) parts.push(`${formatQuantity(result.released_units)} un liberadas`);
  if (result.staged_units > 0) parts.push(`${formatQuantity(result.staged_units)} un por reponer`);
  if (result.courier) parts.push(result.courier);
  return {
    type: result.courier?.startsWith('No') ? 'error' : 'success',
    message: parts.join(' · '),
  };
};

/** The orders on screen as a sheet. */
export const buildOrdersExportRows = (orders) =>
  orders.map((order) => ({
    Orden: order.id,
    Referencia: order.reference_id || '',
    Cliente: order.company?.name || '',
    Bodega: order.warehouse?.name || '',
    Destinatario: order.recipient || '',
    Canal: orderSourceLabel(order.source),
    Estado: wmsOrderStateLabel(order.wms_state),
    'Estado del envío': order.delivery_status || '',
    Pedido: order.requested_units,
    Preparado: order.prepared_units,
    Reservado: order.reserved_units,
    Bultos: order.packages_count,
    Seguimiento: order.tracking_number || '',
    Creada: formatOrderMoment(order.created_at),
    'Stock suficiente': order.processable ? 'Sí' : 'No',
  }));

export const ordersExportFileName = (warehouse) =>
  `ordenes-${warehouse ? warehouse.name : 'todas'}.xlsx`;
